import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Utensils,
  Plus,
  Apple,
  Coffee,
  Sun,
  Moon,
  Flame,
  Droplets,
  Wheat,
} from "lucide-react";

interface MealEntry {
  id: string;
  name: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}

const mealTypes = [
  { id: "breakfast", label: "Breakfast", icon: Coffee },
  { id: "lunch", label: "Lunch", icon: Sun },
  { id: "dinner", label: "Dinner", icon: Moon },
  { id: "snack", label: "Snack", icon: Apple },
];

export default function Nutrition() {
  const { nutritionPlan } = useAuth();
  const [activeTab, setActiveTab] = useState("tracker");
  const [selectedMealType, setSelectedMealType] = useState("breakfast");
  const [newMeal, setNewMeal] = useState({
    name: "",
    calories: "",
    protein: "",
    carbs: "",
    fat: "",
  });
  const [meals, setMeals] = useState<MealEntry[]>([]);

  const addMeal = () => {
    if (!newMeal.name || !newMeal.calories) return;
    
    const meal: MealEntry = {
      id: Date.now().toString(),
      name: newMeal.name,
      calories: parseInt(newMeal.calories) || 0,
      protein: parseInt(newMeal.protein) || 0,
      carbs: parseInt(newMeal.carbs) || 0,
      fat: parseInt(newMeal.fat) || 0,
    };
    
    setMeals([...meals, meal]);
    setNewMeal({ name: "", calories: "", protein: "", carbs: "", fat: "" });
  };

  const totals = meals.reduce(
    (acc, meal) => ({
      calories: acc.calories + meal.calories,
      protein: acc.protein + meal.protein,
      carbs: acc.carbs + meal.carbs,
      fat: acc.fat + meal.fat,
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );

  const goals = nutritionPlan?.macros || { protein: 150, carbs: 200, fat: 65 };
  const calorieGoal = nutritionPlan?.dailyCalories || 2500;

  return (
    <div className="min-h-screen pt-24 pb-12 px-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Nutrition</h1>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8">
            <TabsTrigger value="tracker" className="gap-2">
              <Utensils className="w-4 h-4" />
              Tracker
            </TabsTrigger>
            <TabsTrigger value="plan" className="gap-2">
              <Apple className="w-4 h-4" />
              Meal Plan
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tracker">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Macros Summary */}
              <div className="md:col-span-2 space-y-6">
                <Card className="p-6">
                  <h2 className="font-semibold text-lg mb-6">Today's Summary</h2>
                  
                  {/* Calories */}
                  <div className="mb-6">
                    <div className="flex justify-between mb-2">
                      <span className="flex items-center gap-2">
                        <Flame className="w-4 h-4 text-orange-500" />
                        Calories
                      </span>
                      <span className="text-sm">
                        {totals.calories} / {calorieGoal} kcal
                      </span>
                    </div>
                    <Progress
                      value={(totals.calories / calorieGoal) * 100}
                      className="h-3"
                    />
                  </div>

                  {/* Macros */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Droplets className="w-4 h-4 text-blue-500" />
                        <span className="text-sm">Protein</span>
                      </div>
                      <p className="text-2xl font-bold">{totals.protein}g</p>
                      <p className="text-xs text-muted-foreground">
                        Goal: {goals.protein}g
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Wheat className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm">Carbs</span>
                      </div>
                      <p className="text-2xl font-bold">{totals.carbs}g</p>
                      <p className="text-xs text-muted-foreground">
                        Goal: {goals.carbs}g
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Utensils className="w-4 h-4 text-red-500" />
                        <span className="text-sm">Fat</span>
                      </div>
                      <p className="text-2xl font-bold">{totals.fat}g</p>
                      <p className="text-xs text-muted-foreground">
                        Goal: {goals.fat}g
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Meal List */}
                <Card className="p-6">
                  <h2 className="font-semibold text-lg mb-4">Logged Meals</h2>
                  {meals.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      No meals logged today. Add your first meal!
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {meals.map((meal) => (
                        <div
                          key={meal.id}
                          className="flex items-center justify-between p-3 bg-card/50 rounded-lg"
                        >
                          <div>
                            <p className="font-medium">{meal.name}</p>
                            <p className="text-xs text-muted-foreground">
                              P: {meal.protein}g • C: {meal.carbs}g • F: {meal.fat}g
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{meal.calories}</p>
                            <p className="text-xs text-muted-foreground">kcal</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>

              {/* Add Meal */}
              <Card className="p-6">
                <h2 className="font-semibold text-lg mb-4">Add Meal</h2>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Food Name</Label>
                    <Input
                      placeholder="e.g., Chicken Breast"
                      value={newMeal.name}
                      onChange={(e) =>
                        setNewMeal({ ...newMeal, name: e.target.value })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Calories</Label>
                    <Input
                      type="number"
                      placeholder="kcal"
                      value={newMeal.calories}
                      onChange={(e) =>
                        setNewMeal({ ...newMeal, calories: e.target.value })
                      }
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-2">
                      <Label className="text-xs">Protein (g)</Label>
                      <Input
                        type="number"
                        value={newMeal.protein}
                        onChange={(e) =>
                          setNewMeal({ ...newMeal, protein: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Carbs (g)</Label>
                      <Input
                        type="number"
                        value={newMeal.carbs}
                        onChange={(e) =>
                          setNewMeal({ ...newMeal, carbs: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Fat (g)</Label>
                      <Input
                        type="number"
                        value={newMeal.fat}
                        onChange={(e) =>
                          setNewMeal({ ...newMeal, fat: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  <Button
                    className="w-full gap-2 bg-amber-500 text-black hover:bg-lime-500"
                    onClick={addMeal}
                  >
                    <Plus className="w-4 h-4" />
                    Add to Log
                  </Button>
                </div>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="plan">
            <Card className="p-8 text-center">
              <Apple className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">AI Nutrition Plans Coming Soon</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Get AI-generated meal plans tailored to your fitness goals, dietary preferences,
                and calorie targets.
              </p>
              <Button className="bg-amber-500 text-black hover:bg-lime-500" disabled>
                Generate Nutrition Plan
              </Button>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
